import CompanyListingsSelector from "@/components/company/company-listings-selector";
import CompanyNote from "@/components/company/company-note";
import CompanyUserAssignedSelector from "@/components/company/company-user-assigned-selector";
import CompanyIntegrationsSync from "@/components/integrations/company/company-integrations-sync";
import { Button } from "@/components/ui/button";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO, UserDTO } from "@shaarp/common";
import { PlusIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

const ACTION_ROW_CLASSNAME =
  "w-full h-11 rounded-full bg-majorelle text-majorelle-foreground hover:bg-majorelle/90 hover:text-majorelle-foreground active:brightness-90 border-transparent shadow-lg justify-center";

const ANIMATION_TOTAL_MS = 320;
const STAGGER_STEP_MS = 40;
const ENTER_CLASS =
  "animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-200";
const EXIT_CLASS =
  "animate-out fade-out slide-out-to-bottom-4 fill-mode-both duration-200";

type CompanyMobileActionFabProps = {
  readonly company: CompanyDTO;
  readonly assignedUser: UserDTO | null;
  readonly onAssignedUserChange: (user: UserDTO | null) => void;
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
};

export default function CompanyMobileActionFab({
  company,
  assignedUser,
  onAssignedUserChange,
  expanded,
  onExpandedChange,
}: CompanyMobileActionFabProps) {
  const { t } = useTranslation();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const [isBackdropMounted, setIsBackdropMounted] = useState(expanded);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(expanded);

  useEffect(() => {
    if (expanded) {
      setIsBackdropMounted(true);
      setHasOpenedOnce(true);
      return;
    }
    const id = setTimeout(
      () => setIsBackdropMounted(false),
      ANIMATION_TOTAL_MS,
    );
    return () => clearTimeout(id);
  }, [expanded]);

  const close = () => onExpandedChange(false);
  const toggle = () => onExpandedChange(!expanded);
  // The back gesture folds the menu rather than leaving the company.
  useCloseOnHistoryBack(expanded, close);

  const animClass = expanded ? ENTER_CLASS : EXIT_CLASS;
  const hasConnectedCrm = connectedCrmIntegrations.length > 0;

  // Actions are listed from top of the stack down; entering reverses the
  // order so the bottom-most item appears first and the stack unfurls upward.
  const actions: { key: string; node: ReactNode }[] = [
    {
      key: "note",
      node: (
        <CompanyNote
          company={company}
          className={ACTION_ROW_CLASSNAME}
          onActionComplete={close}
        />
      ),
    },
    {
      key: "listings",
      node: (
        <CompanyListingsSelector
          company={company}
          triggerClassName={ACTION_ROW_CLASSNAME}
          onActionComplete={close}
        />
      ),
    },
    {
      key: "user",
      node: (
        <CompanyUserAssignedSelector
          company={company}
          selectedUser={assignedUser}
          onSelectedUserChange={onAssignedUserChange}
          triggerLabel={t("company.userAssignedSelector.assignCompany")}
          triggerClassName={ACTION_ROW_CLASSNAME}
          onActionComplete={close}
        />
      ),
    },
  ];
  if (hasConnectedCrm) {
    actions.push({
      key: "hubspot",
      node: (
        <CompanyIntegrationsSync
          company={company}
          triggerLabel={t("company.hubspot.sendToHubSpot")}
          triggerClassName={ACTION_ROW_CLASSNAME}
          onActionComplete={close}
        />
      ),
    });
  }

  return (
    <>
      {isBackdropMounted && (
        <button
          type="button"
          aria-label={t("company.mobileActions.closeLabel")}
          className={cn("fixed inset-0 z-40 backdrop-blur-sm", animClass)}
          onClick={close}
        />
      )}
      <div className="fixed bottom-[calc(var(--mobile-bottom-inset)+1rem)] right-4 left-4 z-50 flex flex-col items-stretch gap-3 pointer-events-none">
        {hasOpenedOnce && (
          <div
            className={cn(
              "flex flex-col gap-2",
              expanded ? "pointer-events-auto" : "pointer-events-none",
            )}
          >
            {actions.map((action, index) => {
              const reverseIndex = actions.length - 1 - index;
              const delay = `${(expanded ? reverseIndex : index) * STAGGER_STEP_MS}ms`;
              return (
                <div
                  key={action.key}
                  className={animClass}
                  style={{ animationDelay: delay }}
                >
                  {action.node}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end pointer-events-auto">
          <Button
            type="button"
            size="icon"
            aria-expanded={expanded}
            aria-label={
              expanded
                ? t("company.mobileActions.closeLabel")
                : t("company.mobileActions.openLabel")
            }
            onClick={toggle}
            className="h-14 w-14 rounded-full shadow-xl shadow-black/40 active:brightness-90"
          >
            <PlusIcon
              className={cn(
                "!h-6 !w-6 transition-transform duration-200",
                expanded && "rotate-45",
              )}
            />
          </Button>
        </div>
      </div>
    </>
  );
}
